import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Button } from '../../button/Button';
import { StyledPopper } from '../../menu/Popper/Popper';
import styles from './FiltersPopper.module.scss';
import { Filter, FilterValue } from '../interface';
import { InlineAutocomplete } from '../../input/InlineAutoComplete/InlineAutocomplete';
import { LinkButton } from '../../link/LinkButton';
import { useFilters } from '../hooks/useFilters';
import { FilterRenderOption } from './FilterRenderOption';
import { FilterContentWrapper } from './FilterContentWrapper';
import { getCumulativeFilterCount, getFiltersCount } from '../helpers';
import { getDataIdGenerator } from '../../shared/utils/dataId';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import { FilterTrackers } from '../filter-trackers';
import { ListConductor } from '../../list-conductor/list-conductor';

export interface FilterPopperProps {
  target: string;
  open: boolean;
  filters: Filter[];
  showFooterActions?: boolean;
  listConductor: ListConductor;
  onClose: VoidFunction;
  entity: string;
}

export function FiltersPopper({
  target,
  open,
  filters,
  showFooterActions = true,
  listConductor,
  onClose,
  entity
}: FilterPopperProps) {
  const [activeFilter, setActiveFilter] = useState<Filter | undefined>(undefined);
  const { eventTrack } = useAnalyticsTracker();

  const openFilterContentHandler = (value: Filter) => {
    eventTrack({
      action: FilterTrackers.FILTER_OPEN_FILTER_CLICK,
      properties: {
        entity,
        filterKey: value.filterKey
      }
    });
    setActiveFilter({ ...value });
  };

  const {
    appliedFilters,
    setFilter,
    clearAllFilters,
    applyFilters,
    clearFilter,
    setFiltersFromListMeta
  } = useFilters(listConductor);

  const filterChangeHandler = (filterData: { filterKey: string; value: unknown }) => {
    eventTrack({
      action: FilterTrackers.FILTER_SET_FILTER_ONCHANGE,
      properties: {
        entity,
        filterData
      }
    });
    setFilter(filterData);
  };

  const onCloseHandler = () => {
    eventTrack({
      action: FilterTrackers.FILTER_CANCEL_CTA_CLICK,
      properties: {
        entity,
        appliedFilters
      }
    });
    setActiveFilter(undefined);
    clearAllFilters();
    onClose();
  };

  const applyFiltersHandler = () => {
    eventTrack({
      action: FilterTrackers.FILTER_APPLY_CTA_CLICK,
      properties: {
        entity,
        appliedFilters
      }
    });
    applyFilters();
    setActiveFilter(undefined);
    onClose();
  };

  useEffect(() => {
    const filtersRelatedToPopper = listConductor.getListMeta().filters.filter(filter => {
      return filters.findIndex(filterMeta => filterMeta.filterKey === filter.filter) > -1;
    });
    setFiltersFromListMeta(filtersRelatedToPopper as FilterValue[]);
  }, [open]);

  const clearAllFiltersHandler = () => {
    eventTrack({
      action: FilterTrackers.FILTER_CLEAR_ALL_FILTERS_CTA_CLICK,
      properties: {
        entity,
        appliedFilters
      }
    });
    clearAllFilters();
  };

  const clearFilterHandler = (filterKey: string) => {
    eventTrack({
      action: FilterTrackers.FILTER_CLEAR_FILTER_CTA_CLICK,
      properties: {
        entity,
        filterKey
      }
    });
    clearFilter(filterKey);
  };

  const dataIdGenerator = getDataIdGenerator('filters-popper');
  const filterCount = getCumulativeFilterCount(appliedFilters);
  return (
    <StyledPopper
      dataId={dataIdGenerator('')}
      target={target}
      open={open}
      onClose={() => onCloseHandler()}
    >
      <div className={styles.filtersContainer}>
        <div className={clsx(styles.filtersHeader, 'border-bottom')}>
          <div className='d-flex align-items-center'>
            <p className='text-body-2 text-secondary'>Filters</p>{' '}
            {filterCount ? (
              <span
                data-id={dataIdGenerator('filters-count')}
                className={clsx(styles.cumulativeFilterCount, 'text-body-2')}
              >
                {filterCount}
              </span>
            ) : null}
          </div>

          <LinkButton
            size='md'
            dataId={dataIdGenerator('clear-all')}
            onClick={() => clearAllFiltersHandler()}
          >
            Clear All
          </LinkButton>
        </div>

        <div className={styles.filtersContent}>
          <InlineAutocomplete
            selectMode
            options={filters}
            value={activeFilter}
            isOptionEqualToValue={(option, value) => option.filterKey === value.filterKey}
            onChange={value => {
              openFilterContentHandler(value as Filter);
            }}
            getOptionLabel={(option: string | Filter) =>
              typeof option === 'string' ? option : option.title
            }
            dataId={dataIdGenerator('')}
            renderOption={(props, option) => (
              <FilterRenderOption
                {...props}
                option={option}
                filterCount={getFiltersCount(appliedFilters[option.filterKey])}
              />
            )}
          />
          {activeFilter?.filterKey ? (
            <FilterContentWrapper
              filter={activeFilter}
              onFilterChange={filterChangeHandler}
              appliedFilters={appliedFilters}
              clearFilter={clearFilterHandler}
            />
          ) : null}
        </div>

        {showFooterActions ? (
          <div
            data-id={dataIdGenerator('footer-actions')}
            className={clsx('center-flex-row border-top', styles.footerActions)}
          >
            <Button
              dataId={dataIdGenerator('footer-action-apply')}
              size='sm'
              onClick={() => applyFiltersHandler()}
            >
              Apply
            </Button>
            <Button
              dataId={dataIdGenerator('footer-action-cancel')}
              variation='outline'
              size='sm'
              className='ml-3'
              onClick={() => onCloseHandler()}
            >
              Cancel
            </Button>
          </div>
        ) : null}
      </div>
    </StyledPopper>
  );
}
